/**
 * BookingConfirmation
 * "You're booked!" confirmation dialog with session summary and action buttons
 */

import { Banknote, CalendarDays, Link2, Loader2, MapPin, User } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { useStartExpertGroupChat } from "@/features/chats/hooks/useRitualGroupChat"
import { addCoachingToCalendar } from "@/features/coaching/services/coaching.service"
import { formatDate, formatPrice, formatTime } from "../utils/format"

interface BookingConfirmationProps {
  open: boolean
  onClose: (navigatedToChat?: boolean) => void
  onManageBooking?: () => void
  booking: {
    id: string
    ritualId?: string // Optional - backend uses bookingId for session chats
    ritualTitle: string
    date: string
    startTime: string
    endTime: string
    duration?: string
    location?: string
    sessionType?: string
    coachName: string | null
    websiteUrl?: string
    fee: number | null
    feeCurrency?: string | null
    isCoachingBooking?: boolean // Distinguish coaching from ritual bookings
    expertId?: string // The expert's user ID for opening chat
  }
}

async function addToGoogleCalendar(
  bookingId: string,
  setIsAdding: (loading: boolean) => void,
): Promise<void> {
  setIsAdding(true)
  try {
    const result = await addCoachingToCalendar(bookingId)
    if (result.syncStatus === "synced") {
      toast.success("Added to Google Calendar")
    } else if (result.syncStatus === "pending") {
      toast.success("Event saved — will sync to Google Calendar once connected", {
        duration: 4000,
      })
    } else {
      toast.error("Failed to sync to Google Calendar. Will retry automatically.")
    }
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        status?: number
        data?: { data?: { code?: string }; message?: string }
      }
    }
    const errorMessage = axiosError.response?.data?.message || "Failed to add to Google Calendar"

    if (errorMessage === "Google account not connected") {
      toast.error(errorMessage)
      // Automatically redirect to settings connected-accounts section
      setTimeout(() => {
        window.location.href = "/settings?section=connected-accounts"
      }, 1500)
    } else if (axiosError.response?.status === 409) {
      toast.info("This booking is already in your Google Calendar")
    } else {
      toast.error(errorMessage)
    }
  } finally {
    setIsAdding(false)
  }
}

export function BookingConfirmation({
  open,
  onClose,
  onManageBooking,
  booking,
}: BookingConfirmationProps) {
  const navigate = useNavigate()
  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false)
  const startChat = useStartExpertGroupChat()
  // Cancellation token for the in-flight Done mutation. If the user
  // dismisses the modal (X / Escape — outside-clicks are intentionally
  // disabled here via onInteractOutside.preventDefault) while the
  // mutation is running, we want the eventual onSuccess to bail out
  // instead of yanking them into a chat they explicitly tried to leave.
  const doneTokenRef = useRef<{ canceled: boolean } | null>(null)

  // On unmount (e.g. parent route change), invalidate any pending Done
  // mutation so its onSuccess doesn't fire navigate() into the void.
  useEffect(() => {
    return () => {
      if (doneTokenRef.current) doneTokenRef.current.canceled = true
    }
  }, [])

  const handleManageBooking = () => {
    if (onManageBooking) {
      onManageBooking()
    } else {
      onClose()
      navigate("/my-rituality?tab=sessions&subTab=booked")
    }
  }

  const handleAddToGoogleCalendar = () => {
    addToGoogleCalendar(booking.id, setIsAddingToCalendar)
  }

  // "Done" drops the user straight into the per-booking expert group chat
  // so they can prep for the session immediately. On any failure (network,
  // server, rate limit) we keep the modal open and surface a toast plus
  // the underlying error in the console — the booking itself already
  // succeeded, so we never want a chat-creation hiccup to make the user
  // think the booking didn't.
  //
  // Two race conditions worth knowing:
  //   1. User clicks Done, then X / Escape during the in-flight mutation.
  //      The cancellation token below makes onSuccess bail out so we don't
  //      yank them into a chat they explicitly closed.
  //   2. We `navigate()` BEFORE `onClose()` so React Router commits the
  //      route change first and Radix's Dialog teardown (focus restore +
  //      scroll-lock release) doesn't race the destination chat's mount.
  const handleDone = () => {
    if (startChat.isPending) return
    const token = { canceled: false }
    doneTokenRef.current = token
    startChat.mutate(
      { bookingId: booking.id },
      {
        onSuccess: conversation => {
          if (token.canceled) return
          navigate(`/chats/ritual/${conversation.id}`)
          onClose(true)
        },
        onError: error => {
          if (token.canceled) return
          console.error("startChat failed", error)
          toast.error(
            "Booking confirmed, but we couldn't open the session chat. Find it in Chats > People or try again.",
          )
        },
      },
    )
  }

  // Disable close affordances while the Done mutation is in flight so the
  // user can't dismiss the modal mid-action and end up in the
  // "navigate or not?" race the cancellation token guards against. The
  // cancellation token is the source of truth for correctness; this just
  // closes the most common path before it can happen.
  const isStartingChat = startChat.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (o) return
        if (isStartingChat) return
        onClose()
      }}
    >
      <DialogContent
        className="w-[600px] border-connect-glass-border bg-[#FFFFFF0D] p-5 text-connect-cream"
        overlayClass="bg-[#020202] md:bg-[rgba(2,2,2,0.9)] backdrop-blur-[25px]"
        showCloseButton={!isStartingChat}
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => isStartingChat && e.preventDefault()}
      >
        <div className="flex flex-col items-center text-center">
          <DialogTitle className="text-[24px] font-semibold leading-normal text-[#F2F0EBE6]">
            You're booked!
          </DialogTitle>
          <DialogDescription className="max-w-[430px] mt-4 text-sm leading-6.25 text-[#F2F0EB]">
            Your session is confirmed. We've added it to your schedule and will send you a reminder
            before it begins.
          </DialogDescription>

          <div className="w-full mt-7.5 max-w-[485px] rounded-[15px] border border-[#FFFFFF1A] bg-[#F2F0EB1A] p-5 pt-4">
            <span className="mb-5 text-[16px] font-semibold text-[#F2F0EB] flex">
              {booking.ritualTitle}
            </span>

            <div className="flex flex-col gap-1.25">
              <div className="flex gap-2.5 text-[13px] text-[#F2F0EB]">
                <CalendarDays className="size-5 shrink-0" />
                <span className="text-left">
                  {formatDate(booking.date)} at {formatTime(booking.startTime)} -{" "}
                  {formatTime(booking.endTime)}
                </span>
              </div>

              {booking.location && (
                <div className="flex items-center gap-2.5 text-[13px] text-[#F2F0EB] mt-1">
                  <MapPin className="size-6 shrink-0" />
                  <span className="text-left">{booking.location}</span>
                </div>
              )}

              <div className="flex items-center gap-2.5 text-[13px] text-[#F2F0EB]">
                <User className="size-6 shrink-0" />
                <span className="text-left">
                  {booking.sessionType || "Private session"} with{" "}
                  <span className="font-semibold text-connect-cream">
                    {booking.coachName || "Coach"}
                  </span>
                </span>
              </div>

              {booking.websiteUrl && (
                <div className="flex items-center gap-2.5 text-[13px] text-connect-cream/70">
                  <Link2 className="size-5 shrink-0" />
                  <a
                    href={booking.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {booking.websiteUrl}
                  </a>
                </div>
              )}

              <div className="text-[14px] font-semibold text-brand-terracotta flex items-center gap-2.5">
                <Banknote className="size-6 shrink-0" />
                {formatPrice(booking.fee, booking.feeCurrency)}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-3.75 mt-7.5">
            <Button
              variant="primary-white"
              onClick={handleDone}
              disabled={isStartingChat}
              className="h-[50px] py-[15px] text-sm font-semibold uppercase w-full sm:max-w-[280px] flex items-center justify-center gap-2"
            >
              {isStartingChat && <Loader2 className="size-4 animate-spin" />}
              <span>{isStartingChat ? "Opening chat..." : "Done"}</span>
            </Button>
            {/* Add to Calendar button - only for coaching bookings */}
            <Button
              variant="outline"
              onClick={handleAddToGoogleCalendar}
              disabled={isAddingToCalendar}
              className="flex h-[50px] py-[15px] items-center justify-center gap-2 text-[14px] font-semibold uppercase w-full sm:max-w-[280px]"
            >
              <CalendarDays className="size-4" />
              <span>{isAddingToCalendar ? "Adding..." : "Add to Calendar"}</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleManageBooking}
              className="flex h-[50px] py-[15px] items-center justify-center gap-2 text-[14px] font-semibold uppercase w-full sm:max-w-[280px]"
            >
              Manage Booking
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
